import React, { useRef, useState } from 'react';
import Icon from '../Icon/Icon';
import './FileUpload.scss';

export interface FileUploadProps {
  name: string;
  label: string;
  value?: string | null;
  onChange: (file: File | null, previewUrl: string | null) => void;
  error?: string;
  accept?: string;
  maxSize?: number; // in MB
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  accept = 'image/*',
  maxSize = 5, // 5MB default
  disabled = false,
  placeholder,
  required = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);

  const handleFileSelect = (file: File) => {
    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      alert(`El archivo es demasiado grande. Tamaño máximo: ${maxSize}MB`);
      return;
    }

    // Validate file type
    if (accept && !file.type.match(accept.replace('*', '.*'))) {
      alert('Tipo de archivo no válido');
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onChange(file, url);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onChange(null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="file-upload">
      <label className="file-upload__label">
        {label}
        {required && <span className="file-upload__required">*</span>}
      </label>
      
      <div
        className={`file-upload__dropzone ${dragActive ? 'file-upload__dropzone--active' : ''} ${disabled ? 'file-upload__dropzone--disabled' : ''} ${error ? 'file-upload__dropzone--error' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          name={name}
          accept={accept}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="file-upload__input"
        />
        
        {previewUrl ? (
          <div className="file-upload__preview">
            <img src={previewUrl} alt="Preview" className="file-upload__preview-image" />
            <div className="file-upload__preview-overlay">
              <button
                type="button"
                className="file-upload__remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                disabled={disabled}
              >
                <Icon name="cerrar" size={16} alt="Remove" />
              </button>
            </div>
          </div>
        ) : (
          <div className="file-upload__placeholder">
            <Icon name="imagen" size={32} alt="Upload" />
            <p className="file-upload__placeholder-text">
              {placeholder || 'Arrastra una imagen aquí o haz clic para seleccionar'}
            </p>
            <p className="file-upload__placeholder-hint">
              Tamaño máximo: {maxSize}MB
            </p>
          </div>
        )}
      </div>
      
      {error && (
        <p className="file-upload__error">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;
